import { api } from "../../api";
import { useCallback, useMemo } from "react";
import { ProductEntry } from "../../types/shared.ts";
import { useDataGridQuery, useUrlState } from "../../hooks/useUrlState.ts";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.tsx";
import { TableDialogs } from "../../components/TableDialogs.tsx";
import { ViewProvider } from "../../contexts/ViewContext.tsx";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { productsConfig as config } from "./productsConfig.ts";
import { PRODUCTS_DEFAULTS } from "../../helpers/defaultUrl.ts";
import { productsFromExternalList } from "./converts.ts";
import ProductsToolbar from "./ProductsToolbar.tsx";
import ProductsTable from "./ProductsTable.tsx";

const columns = config.columns;

export default function ProductsPage() {
  const { globalFilterValue, setGlobalFilterValue } = useUrlState();
  const debouncedSearchValue = useDebouncedValue(globalFilterValue, 300);

  const { page, limit, sort, order, onPageChange, onSortChange } =
    useDataGridQuery({
      page: PRODUCTS_DEFAULTS.page,
      limit: PRODUCTS_DEFAULTS.limit,
      sort: PRODUCTS_DEFAULTS.sort,
      order: PRODUCTS_DEFAULTS.order,
    });

  const { data: rawData, isLoading } =
    api.endpoints.listProductsApiProductsGet.useQuery({
      orderBy: "name",
      limit: limit,
      offset: page * limit,
      search: debouncedSearchValue || undefined,
      searchFields: "name",
    });

  const data: ProductEntry[] = useMemo(() => {
    if (!rawData) {
      return [];
    }
    return rawData.items.map(productsFromExternalList);
  }, [rawData]);

  const handleSortChange = useCallback(
    (e: DataTableSortEvent) => onSortChange(e.sortField, e.sortOrder ?? 1),
    [onSortChange],
  );
  const handlePageChange = useCallback(
    (e: DataTablePageEvent) => onPageChange(e.page ?? 0, e.rows ?? limit),
    [onPageChange, limit],
  );

  return (
    <ViewProvider view={config.view}>
      <TableDialogs />
      <ConfirmDialog />
      <ProductsToolbar
        config={config}
        globalFilterValue={globalFilterValue}
        setGlobalFilterValue={setGlobalFilterValue}
      />
      <ProductsTable
        data={data}
        columns={columns}
        page={page}
        rowsPerPage={limit}
        onPage={handlePageChange}
        totalRecords={rawData?.total || 0}
        sortField={sort}
        sortOrder={order}
        onSort={handleSortChange}
        isLoading={isLoading}
      />
    </ViewProvider>
  );
}
